import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Download, Printer, Share2, Copy, CheckCircle } from "lucide-react";

export default function QRMascota() {
    const { userId, mascotaId } = useParams();
    const navigate = useNavigate();
    const [mascota, setMascota] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const qrRef = useRef();

    useEffect(() => {
        cargarMascota();
    }, [mascotaId]);

    const cargarMascota = async () => {
        try {
            const res = await api.getMascota(mascotaId);
            setMascota(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // URL pública con mayúscula en "MascotaProtegida"
    const urlPublica = mascota
        ? `https://tagsmartpet.com/MascotaProtegida/${mascota.id}`
        : "";

    const descargarQR = () => {
        const canvas = qrRef.current?.querySelector("canvas");
        if (!canvas) return;
        const link = document.createElement("a");
        link.download = `QR-${mascota?.nombre || "mascota"}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    const imprimirQR = () => {
        const canvas = qrRef.current?.querySelector("canvas");
        if (!canvas) return;
        const ventana = window.open("", "_blank");
        if (!ventana) return;
        ventana.document.write(`
            <html>
                <head><title>QR de ${mascota?.nombre}</title></head>
                <body style="display:flex;justify-content:center;align-items:center;min-height:100vh;margin:0;background:#f5f5f5;">
                    <img src="${canvas.toDataURL("image/png")}" style="max-width:80%;border:2px solid #ddd;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.1);" />
                </body>
            </html>
        `);
        ventana.document.close();
        ventana.focus();
        ventana.print();
    };

    const compartir = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `QR de ${mascota?.nombre || "mascota"}`,
                    text: `Escaneá este código para ver el perfil de ${mascota?.nombre}`,
                    url: urlPublica,
                });
            } catch (err) {
                // El usuario canceló el share
            }
        } else {
            copiarEnlace();
        }
    };

    const copiarEnlace = async () => {
        try {
            await navigator.clipboard.writeText(urlPublica);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            alert("Copiá este enlace: " + urlPublica);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            </div>
        );
    }

    if (!mascota || !mascota.id) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
                <div className="bg-white rounded-2xl p-8 text-center shadow-lg max-w-sm w-full">
                    <p className="text-gray-500 font-semibold">Mascota no encontrada</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 text-indigo-600 font-bold hover:underline"
                    >
                        Volver atrás
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-6 flex items-center justify-center">
            <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-6 text-center">
                {/* Header */}
                <div className="mb-4">
                    <h1 className="text-2xl font-black text-gray-800">
                        QR de {mascota.nombre}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Escaneá este código para ver el perfil público de tu mascota
                    </p>
                </div>

                {/* QR */}
                <div className="flex justify-center mb-6">
                    <div
                        ref={qrRef}
                        className="bg-white p-3 rounded-2xl shadow-md border border-gray-100 inline-block"
                    >
                        <QRCodeCanvas
                            value={urlPublica}
                            size={220}
                            bgColor="#ffffff"
                            fgColor="#1e1e2f"
                            level="H"
                            includeMargin
                        />
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="flex flex-wrap justify-center gap-3 mb-4">
                    <button
                        onClick={descargarQR}
                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-md transition"
                    >
                        <Download className="w-4 h-4" /> Descargar
                    </button>
                    <button
                        onClick={imprimirQR}
                        className="flex items-center gap-2 bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-5 py-2.5 rounded-full font-bold text-sm transition"
                    >
                        <Printer className="w-4 h-4" /> Imprimir
                    </button>
                    <button
                        onClick={compartir}
                        className="flex items-center gap-2 bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-5 py-2.5 rounded-full font-bold text-sm transition"
                    >
                        <Share2 className="w-4 h-4" /> Compartir
                    </button>
                </div>

                {/* Enlace y copiar */}
                <div className="flex items-center justify-center gap-2 bg-gray-50 rounded-xl p-3 text-sm text-gray-600 border border-gray-100">
                    <span className="truncate max-w-[200px]">{urlPublica}</span>
                    <button
                        onClick={copiarEnlace}
                        className="p-1.5 rounded-lg hover:bg-gray-200 transition flex-shrink-0"
                        title="Copiar enlace"
                    >
                        {copied ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                            <Copy className="w-4 h-4 text-gray-500" />
                        )}
                    </button>
                </div>

                {copied && (
                    <p className="text-xs text-green-600 mt-2 font-semibold">
                        ¡Enlace copiado al portapapeles!
                    </p>
                )}
            </div>
        </div>
    );
}